import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, XCircle, FileText, Loader2, Upload, ExternalLink, Download } from 'lucide-react';
import api from '../../utils/axios';
import getFullUrl from '../../utils/formatUrl';

const NoticeManager = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    date: new Date().toISOString().split('T')[0],
    pdfUrl: '',
    program: 'General',
    isActive: true,
  });

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    try {
      setLoading(true);
      const res = await api.get('/notices');
      setNotices(res.data.data);
    } catch (err) {
      console.error('Failed to fetch notices', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if it's a PDF
    if (file.type !== 'application/pdf') {
      alert("Only PDF files are allowed!");
      return;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    setUploading(true);
    try {
      const res = await api.post('/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, pdfUrl: res.data.url }));
    } catch (err) {
      alert("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.pdfUrl) {
      alert("Please upload a PDF file");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/notices/${editingId}`, formData);
      } else {
        await api.post('/notices', formData);
      }

      setShowForm(false);
      setEditingId(null);
      setFormData({ 
        title: '', 
        date: new Date().toISOString().split('T')[0], 
        pdfUrl: '', 
        program: 'General', 
        isActive: true 
      });
      fetchNotices();
    } catch (err) {
      alert('Failed to save notice');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (notice) => {
    setFormData({
      title: notice.title,
      date: notice.date ? new Date(notice.date).toISOString().split('T')[0] : '',
      pdfUrl: notice.pdfUrl || '',
      program: notice.program || 'General',
      isActive: notice.isActive,
    });
    setEditingId(notice._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this notice?')) {
      try {
        await api.delete(`/notices/${id}`);
        setNotices(notices.filter(n => n._id !== id));
      } catch (err) {
        alert('Failed to delete');
      }
    }
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ 
      title: '', 
      date: new Date().toISOString().split('T')[0], 
      pdfUrl: '', 
      program: 'General', 
      isActive: true 
    });
  };

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-[#003366] to-[#004080] text-white p-8 rounded-sm relative overflow-hidden shadow-2xl">
        <div className="relative z-10 flex justify-between items-center">
          <div>
            <h4 className="text-2xl font-black uppercase tracking-tighter mb-2 italic flex items-center">
              <FileText size={28} className="mr-3" /> Notice Board Management
            </h4>
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/70 max-w-xl">
              Upload institutional notices, exam schedules, and official circulars for students.
            </p>
          </div>
          <button
            onClick={() => { cancelForm(); setShowForm(true); }}
            className="bg-amber-500 text-white px-6 py-3 rounded-sm font-black uppercase text-xs tracking-widest flex items-center hover:bg-white hover:text-amber-500 transition-all shadow-xl"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Notice
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-8 animate-scaleIn">
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-lg font-black uppercase tracking-tighter text-slate-900">
              {editingId ? 'Edit Notice' : 'Add New Notice'}
            </h3>
            <button onClick={cancelForm} className="text-slate-400 hover:text-red-500">
              <XCircle size={22} />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Notice Title</label>
                <input
                  type="text"
                  required
                  className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-amber-500 font-bold text-sm"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g. B.Ed Exam Schedule 2026"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Date</label>
                <input
                  type="date"
                  required
                  className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-amber-500 font-bold text-sm"
                  value={formData.date}
                  onChange={e => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Program Category</label>
                <select
                  className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-amber-500 font-bold text-sm bg-transparent"
                  value={formData.program}
                  onChange={e => setFormData({ ...formData, program: e.target.value })}
                >
                  <option value="B.ED">B.ED</option>
                  <option value="M.ED">M.ED</option>
                  <option value="Other Program">Other Program</option>
                  <option value="General">General</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Upload Notice (PDF ONLY)</label>
                <div className="flex items-center space-x-4">
                  <label className={`flex-1 cursor-pointer px-4 py-3 bg-slate-50 border-2 border-dashed border-slate-200 rounded-sm text-xs font-bold text-slate-500 hover:border-amber-500 transition-all flex items-center justify-center ${uploading ? 'opacity-50' : ''}`}>
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                    {uploading ? 'Uploading PDF...' : formData.pdfUrl ? 'PDF Uploaded (Click to change)' : 'Select PDF File'}
                    <input type="file" className="hidden" accept="application/pdf" onChange={handleFileUpload} disabled={uploading} />
                  </label>
                  {formData.pdfUrl && (
                    <a 
                      href={getFullUrl(formData.pdfUrl)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-3 bg-amber-50 text-amber-500 rounded-sm hover:bg-amber-500 hover:text-white transition-all shadow-sm"
                      title="Preview PDF"
                    >
                      <ExternalLink size={20} />
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-4 h-4 accent-amber-500"
              />
              <label htmlFor="isActive" className="text-sm font-bold text-slate-700">Display on Notice Board</label>
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={saving || uploading}
                className="bg-[#003366] text-white px-10 py-4 font-black uppercase text-xs tracking-[3px] shadow-xl hover:bg-amber-500 transition-all disabled:opacity-50 flex items-center"
              >
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> {editingId ? 'Update' : 'Save'} Notice</>}
              </button>
              <button
                type="button"
                onClick={cancelForm}
                className="bg-slate-100 text-slate-600 px-8 py-4 font-black uppercase text-xs tracking-widest hover:bg-slate-200 transition-all"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
          <h3 className="text-sm font-black uppercase tracking-widest text-slate-600">
            Active Notices ({notices.length})
          </h3>
        </div>

        {loading ? (
          <div className="p-20 text-center">
            <Loader2 size={32} className="animate-spin text-slate-300 mx-auto" />
            <p className="text-slate-400 font-bold text-xs mt-4 uppercase tracking-widest">Retrieving notices...</p>
          </div>
        ) : notices.length === 0 ? (
          <div className="p-20 text-center">
            <FileText size={48} className="text-slate-200 mx-auto mb-4" />
            <p className="text-slate-400 font-bold text-sm">No notices uploaded yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 text-left border-b border-slate-100">
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Notice Detail</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Program</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Date</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Status</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {notices.map((notice) => (
                  <tr key={notice._id} className="hover:bg-slate-50/80 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-red-50 text-red-600 rounded-sm">
                          <FileText size={20} />
                        </div>
                        <div>
                          <p className="text-sm font-black text-[#003366] uppercase tracking-tight">{notice.title}</p>
                          <a 
                            href={getFullUrl(notice.pdfUrl)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-[10px] font-bold text-amber-500 hover:text-amber-600 flex items-center mt-1 uppercase"
                          >
                            <Download size={10} className="mr-1" /> View PDF Attachment
                          </a>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 bg-slate-100 text-[#003366] rounded-sm text-[10px] font-black uppercase tracking-tighter">
                        {notice.program}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                        {new Date(notice.date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase ${notice.isActive ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
                        {notice.isActive ? 'Visible' : 'Hidden'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(notice)}
                          className="p-2 text-slate-400 hover:text-[#003366] hover:bg-slate-100 rounded-sm transition-all shadow-sm border border-slate-100 bg-white"
                        >
                          <Save size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(notice._id)}
                          className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-sm transition-all shadow-sm border border-slate-100 bg-white"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default NoticeManager;
